import React, { useEffect, useState } from 'react';
import { getApi } from '../utils/api';
import { Timestamp } from './ui/timestamp';

interface ImportJobStatusProps {
  importJobId: string;
  onComplete?: (result: any) => void;
  pollingInterval?: number; // ms
}

const ImportJobStatus: React.FC<ImportJobStatusProps> = ({
  importJobId,
  onComplete,
  pollingInterval = 1000, // Changed from 3000 to 1000ms (1 second) for more real-time updates
}) => {
  const [jobData, setJobData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState<boolean>(true);
  const [retryCount, setRetryCount] = useState<number>(0);
  const MAX_RETRIES = 5; // Maximum number of retries for 404 errors

  const api = getApi();

  useEffect(() => {
    let intervalId: number | null = null;
    
    const checkStatus = async () => {
      try {
        const response = await api.get(`/v1/import/job/${importJobId}`);
        
        // Reset retry count on success
        setRetryCount(0);
        setJobData(response.data);
        setLoading(false);
        
        // Check if job is completed or failed
        if (response.data.status === 'completed' || response.data.status === 'failed') {
          setIsPolling(false);
          if (onComplete) onComplete(response.data);
        }
      } catch (err: any) {
        console.error('Error checking import job status:', err);
        
        // Special handling for 404 errors - could be a race condition
        // where the import job record hasn't been created yet
        if (err.message?.includes('404') && retryCount < MAX_RETRIES) {
          console.log(`Import job not found yet, retrying (${retryCount + 1}/${MAX_RETRIES})...`);
          setRetryCount(prev => prev + 1);
          // Continue polling for 404 errors until max retries reached
        } else {
          // For other errors or if max retries reached, stop polling
          setError(retryCount >= MAX_RETRIES 
            ? 'Import job not found after multiple attempts. The import may have failed to start properly.' 
            : 'Failed to fetch import status');
          setLoading(false);
          setIsPolling(false);
        }
      }
    };
    
    if (isPolling && importJobId) {
      // Check immediately
      checkStatus();
      // Then start polling
      intervalId = window.setInterval(checkStatus, pollingInterval);
    }
    
    return () => {
      if (intervalId !== null) clearInterval(intervalId);
    };
  }, [importJobId, isPolling, pollingInterval, onComplete, retryCount]);

  if (loading) {
    return (
      <div className="flex justify-center p-4">
        <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 bg-red-50 text-red-700 rounded border border-red-200">
        <span>{error}</span>
        {error.includes('Import job not found') && (
          <div className="mt-2 text-sm">
            <p>This might happen if the import is taking longer than expected to initialize.</p>
            <button 
              onClick={() => {
                setError(null);
                setLoading(true);
                setIsPolling(true);
                setRetryCount(0);
              }}
              className="mt-1 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!jobData) {
    return (
      <div className="p-3 bg-red-50 text-red-700 rounded border border-red-200">
        <span>Import job not found</span>
      </div>
    );
  }

  // Calculate progress
  const progress = jobData.total_count > 0 
    ? Math.round((jobData.processed_count / jobData.total_count) * 100) 
    : 0;

  return (
    <div className="space-y-4 p-4 border rounded-md">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Import Job: {jobData.filename}</h3>
        <span className={`px-2 py-1 rounded text-sm ${
          jobData.status === 'completed' ? 'bg-green-100 text-green-800' :
          jobData.status === 'failed' ? 'bg-red-100 text-red-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {jobData.status.charAt(0).toUpperCase() + jobData.status.slice(1)}
        </span>
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span>Progress</span>
          <span>{jobData.processed_count} / {jobData.total_count} records</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full ${
              jobData.status === 'completed' ? 'bg-green-600' :
              jobData.status === 'failed' ? 'bg-red-600' :
              'bg-blue-600'
            }`}
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="font-medium">Started:</span>
          <span className="ml-2"><Timestamp value={jobData.created_at} format="absolute" /></span>
        </div>
        <div>
          <span className="font-medium">Last updated:</span>
          <span className="ml-2"><Timestamp value={jobData.updated_at} format="absolute" /></span>
        </div>
        <div>
          <span className="font-medium">Successfully processed:</span>
          <span className="ml-2 text-green-600">{jobData.processed_count}</span>
        </div>
        <div>
          <span className="font-medium">Failed:</span>
          <span className="ml-2 text-red-600">{jobData.failed_count}</span>
        </div>
      </div>
      
      {jobData.errors && jobData.errors.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium mb-2">Errors:</h4>
          <div className="max-h-40 overflow-y-auto bg-gray-50 rounded border p-2">
            <ul className="list-disc pl-5 text-sm">
              {jobData.errors.map((err: string, idx: number) => (
                <li key={idx} className="text-red-600">
                  {err}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportJobStatus;